import React, { useState, useEffect, useRef } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { LogOut, MessageCircle, User, Calendar, Mail, Users, UserPlus, Shield, Home, Lock, Unlock, Ticket, Copy, Check } from 'lucide-react';
import { getAdminFeedbacks, getAllUsers, makeUserAdmin, generatePromoCodes, listPromoCodes, PromoCodeItem, sendAdminSnapshot, getAdminSnapshotPreview } from '../services/api';
import SnapshotLetter, { normalizeSnapshotLetter } from './SnapshotLetter';
import { blobToBase64 } from '../services/pagePdf';
import { useAuth } from '../App';

interface AdminDashboardProps {
  currentUser: any;
  onLogout: () => void;
  onSwitchToDashboard?: () => void;
}

interface Feedback {
  answers: Record<string, string>;
  child_id: string;
  email: string;
  timestamp: string;
  user_id: string;
}

interface UserData {
  email: string;
  joinedDate: string;
}

export const AdminDashboard: React.FC<AdminDashboardProps> = ({ currentUser, onLogout, onSwitchToDashboard }) => {
  const { isAdmin, adminBypassPayment, setAdminBypassPayment } = useAuth();
  const [bypassSaving, setBypassSaving] = useState(false);
  const [bypassError, setBypassError] = useState('');
  const [feedbacks, setFeedbacks] = useState<Feedback[]>([]);
  const [count, setCount] = useState(0);
  const [users, setUsers] = useState<UserData[]>([]);
  const [userCount, setUserCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedFeedback, setSelectedFeedback] = useState<Feedback | null>(null);
  const [activeTab, setActiveTab] = useState<'feedbacks' | 'users' | 'makeAdmin' | 'promoCodes' | 'sendSnapshot'>('feedbacks');
  const [newAdminEmail, setNewAdminEmail] = useState('');
  const [isAddingAdmin, setIsAddingAdmin] = useState(false);
  const [adminMessage, setAdminMessage] = useState({ type: '', text: '' });

  // Promo codes state
  const [promoCodes, setPromoCodes] = useState<PromoCodeItem[]>([]);
  const [promoStats, setPromoStats] = useState({ total: 0, active: 0, used: 0 });
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoError, setPromoError] = useState('');
  const [generateCount, setGenerateCount] = useState(1);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedCodes, setGeneratedCodes] = useState<string[]>([]);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  // Send-snapshot state
  const [snapshotParentEmail, setSnapshotParentEmail] = useState('');
  const [snapshotToEmail, setSnapshotToEmail] = useState('');
  const [snapshotChildId, setSnapshotChildId] = useState('');
  const [isSendingSnapshot, setIsSendingSnapshot] = useState(false);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [snapshotMessage, setSnapshotMessage] = useState({ type: '', text: '' });
  const [snapshotPreview, setSnapshotPreview] = useState<any>(null);
  const snapshotCaptureRef = useRef<(() => Promise<Blob>) | null>(null);

  useEffect(() => {
    loadFeedbacks();
    loadUsers();
  }, []);

  useEffect(() => {
    if (activeTab === 'promoCodes' && promoCodes.length === 0 && !promoLoading) {
      loadPromoCodes();
    }
  }, [activeTab]);

  const loadFeedbacks = async () => {
    setIsLoading(true);
    setError('');
    try {
      const data = await getAdminFeedbacks();
      setCount(data.count || 0);
      // Sort feedbacks by timestamp in descending order (most recent first)
      const sortedFeedbacks = (data.feedbacks || []).sort((a: Feedback, b: Feedback) => 
        new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      );
      setFeedbacks(sortedFeedbacks);
    } catch (err: any) {
      setError(err.message || 'Failed to load feedbacks');
    } finally {
      setIsLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const data = await getAllUsers();
      setUserCount(data.count || 0);
      setUsers(data.users || []);
    } catch (err: any) {
      console.error('Error loading users:', err);
      // Don't set error as this might not be implemented yet
    }
  };

  const handleMakeAdmin = async () => {
    if (!newAdminEmail.trim()) {
      setAdminMessage({ type: 'error', text: 'Please enter an email address' });
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newAdminEmail)) {
      setAdminMessage({ type: 'error', text: 'Please enter a valid email address' });
      return;
    }

    setIsAddingAdmin(true);
    setAdminMessage({ type: '', text: '' });

    try {
      const response = await makeUserAdmin(newAdminEmail);
      setAdminMessage({ 
        type: 'success', 
        text: `Successfully made ${newAdminEmail} an admin!` 
      });
      setNewAdminEmail('');
      
      // Clear success message after 5 seconds
      setTimeout(() => {
        setAdminMessage({ type: '', text: '' });
      }, 5000);
    } catch (err: any) {
      setAdminMessage({ 
        type: 'error', 
        text: err.response?.data?.message || err.message || 'Failed to make user admin. Please try again.' 
      });
    } finally {
      setIsAddingAdmin(false);
    }
  };

  const handleToggleBypass = async () => {
    setBypassSaving(true);
    setBypassError('');
    try {
      await setAdminBypassPayment(!adminBypassPayment);
    } catch (err: any) {
      setBypassError(err?.message || 'Failed to update bypass mode');
    } finally {
      setBypassSaving(false);
    }
  };

  const loadPromoCodes = async () => {
    setPromoLoading(true);
    setPromoError('');
    try {
      const data = await listPromoCodes();
      setPromoCodes(data.codes || []);
      setPromoStats({ total: data.total, active: data.active, used: data.used });
    } catch (err: any) {
      setPromoError(err?.response?.data?.detail || err?.message || 'Failed to load promo codes');
    } finally {
      setPromoLoading(false);
    }
  };

  const handleGeneratePromoCodes = async () => {
    const count = Math.max(1, Math.min(500, Math.floor(generateCount) || 1));
    setIsGenerating(true);
    setPromoError('');
    setGeneratedCodes([]);
    try {
      const data = await generatePromoCodes(count);
      setGeneratedCodes(data.codes || []);
      await loadPromoCodes();
    } catch (err: any) {
      setPromoError(err?.response?.data?.detail || err?.message || 'Failed to generate promo codes');
    } finally {
      setIsGenerating(false);
    }
  };

  const snapshotErrorText = (err: any, fallback: string) => {
    const detail = err?.response?.data;
    const children = detail?.details?.children;
    const hint = Array.isArray(children) && children.length
      ? ` Children: ${children.map((c: any) => `${c.name || '?'} (${c.child_id})`).join(', ')}`
      : '';
    return (detail?.message || detail?.detail || err?.message || fallback) + hint;
  };

  const handleLoadSnapshotPreview = async () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(snapshotParentEmail.trim())) {
      setSnapshotMessage({ type: 'error', text: 'Enter the parent account email' });
      return;
    }
    setIsLoadingPreview(true);
    setSnapshotMessage({ type: '', text: '' });
    setSnapshotPreview(null);
    try {
      const data = await getAdminSnapshotPreview(
        snapshotParentEmail.trim(),
        snapshotChildId.trim() || undefined
      );
      const letter = normalizeSnapshotLetter(data?.snapshot);
      if (!letter) {
        setSnapshotMessage({ type: 'error', text: 'This account has no snapshot letter to show yet.' });
        return;
      }
      setSnapshotPreview({ letter, answers: data?.answers, childId: data?.child_id, grade: data?.grade });
    } catch (err: any) {
      setSnapshotMessage({ type: 'error', text: snapshotErrorText(err, 'Failed to load the snapshot') });
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const handleSendSnapshot = async () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(snapshotToEmail.trim())) {
      setSnapshotMessage({ type: 'error', text: 'Enter a valid recipient email' });
      return;
    }
    if (!snapshotCaptureRef.current) {
      setSnapshotMessage({ type: 'error', text: 'Load the preview first, then send.' });
      return;
    }

    setIsSendingSnapshot(true);
    setSnapshotMessage({ type: '', text: '' });
    try {
      // The PDF is the page itself - the same pixels the parent sees.
      const blob = await snapshotCaptureRef.current();
      const pagePdf = await blobToBase64(blob);
      const data = await sendAdminSnapshot(
        snapshotParentEmail.trim(),
        snapshotToEmail.trim(),
        pagePdf,
        snapshotPreview?.childId || snapshotChildId.trim() || undefined
      );
      setSnapshotMessage({
        type: 'success',
        text: data.message || `Snapshot sent to ${snapshotToEmail.trim()}`,
      });
    } catch (err: any) {
      setSnapshotMessage({ type: 'error', text: snapshotErrorText(err, 'Failed to send the snapshot') });
    } finally {
      setIsSendingSnapshot(false);
    }
  };

  const copyToClipboard = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(null), 1500);
    } catch {
      // ignore clipboard errors
    }
  };

  const formatDate = (timestamp: string) => {
    return new Date(timestamp).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-[#F7B26A] border-t-transparent"></div>
          <p className="mt-4 text-lg text-[#1F224B]">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6 bg-[#FFF9F3]">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <header className="w-full bg-gradient-to-r from-[#F7B26A] to-[#ECA7C1] text-white font-bold py-4 px-6 rounded-3xl shadow-xl">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-xl flex items-center gap-3">
                  <span className="inline-block w-8 h-8 rounded-full bg-white/30 flex items-center justify-center text-lg">👨‍💼</span>
                  Admin Dashboard
                </h1>
                <p className="text-xs opacity-90 mt-1">Welcome, {currentUser?.name || currentUser?.email}</p>
              </div>
              <div className="flex items-center gap-2">
                {onSwitchToDashboard && (
                  <button 
                    onClick={onSwitchToDashboard}
                    className="flex items-center gap-2 bg-white/90 text-[#1F224B] px-3 py-1.5 rounded-lg shadow-sm hover:brightness-95 transition-all font-semibold text-sm"
                  >
                    <Home className="w-4 h-4" />
                    <span className="hidden md:inline">User Dashboard</span>
                  </button>
                )}
                <button 
                  onClick={onLogout}
                  className="flex items-center gap-2 bg-white/90 text-[#1F224B] px-3 py-1.5 rounded-lg shadow-sm hover:brightness-95 transition-all font-semibold text-sm"
                >
                  <LogOut className="w-4 h-4" />
                  <span>Logout</span>
                </button>
              </div>
            </div>
          </header>
        </div>

        {/* Stats Card */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <Card className="p-6 bg-white/95 backdrop-blur border-4 border-[#F0EEFF] rounded-3xl shadow-xl">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-gradient-to-r from-[#F7B26A] to-[#ECA7C1] rounded-full flex items-center justify-center shadow-lg">
                <MessageCircle className="w-8 h-8 text-white" />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-[#1F224B]">{count}</h2>
                <p className="text-[#6B6FA3] font-medium">Total Feedbacks</p>
              </div>
            </div>
          </Card>

          <Card className="p-6 bg-white/95 backdrop-blur border-4 border-[#F0EEFF] rounded-3xl shadow-xl">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-gradient-to-r from-[#A6A1F7] to-[#8E7BFF] rounded-full flex items-center justify-center shadow-lg">
                <Users className="w-8 h-8 text-white" />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-[#1F224B]">{userCount}</h2>
                <p className="text-[#6B6FA3] font-medium">Registered Users</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Bypass Payment Toggle */}
        {isAdmin && (
          <Card className="p-6 mb-6 bg-white/95 backdrop-blur border-4 border-[#F0EEFF] rounded-3xl shadow-xl">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex items-start gap-4 min-w-0 flex-1">
                <div className={`w-14 h-14 rounded-full flex items-center justify-center shadow-lg flex-none ${
                  adminBypassPayment
                    ? 'bg-gradient-to-r from-[#F7B26A] to-[#F76C8A]'
                    : 'bg-gradient-to-r from-[#A6A1F7] to-[#8E7BFF]'
                }`}>
                  {adminBypassPayment
                    ? <Unlock className="w-7 h-7 text-white" />
                    : <Lock className="w-7 h-7 text-white" />}
                </div>
                <div className="min-w-0">
                  <h2 className="text-lg font-bold text-[#1F224B] flex items-center gap-2">
                    Bypass Payment Mode
                  </h2>
                  <p className="text-sm text-[#6B6FA3] mt-1">
                    When <span className="font-semibold text-[#1F224B]">ON</span>, you can open and take
                    any child's assessments without payment — useful for reviewing the full flow.
                    When <span className="font-semibold text-[#1F224B]">OFF</span>, you go through the
                    normal payment flow like any other parent.
                  </p>
                  {bypassError && (
                    <p className="text-sm text-red-600 mt-2">{bypassError}</p>
                  )}
                </div>
              </div>
              <button
                onClick={handleToggleBypass}
                disabled={bypassSaving}
                className={`relative inline-flex h-9 w-16 flex-none items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[#F7B26A] focus:ring-offset-2 disabled:opacity-50 ${
                  adminBypassPayment ? 'bg-gradient-to-r from-[#F7B26A] to-[#F76C8A]' : 'bg-gray-300'
                }`}
                role="switch"
                aria-checked={adminBypassPayment}
                aria-label="Toggle bypass payment mode"
                title={adminBypassPayment ? 'Bypass is ON — click to turn off' : 'Bypass is OFF — click to turn on'}
              >
                <span
                  className={`inline-block h-7 w-7 transform rounded-full bg-white shadow-md transition-transform duration-200 ${
                    adminBypassPayment ? 'translate-x-8' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
            <div className={`mt-3 text-xs font-semibold inline-flex items-center gap-1 px-2 py-0.5 rounded-full ${
              adminBypassPayment
                ? 'text-green-700 bg-green-50 border border-green-200'
                : 'text-[#6B6FA3] bg-[#F5F3FF] border border-[#EDE9FF]'
            }`}>
              {adminBypassPayment ? 'Bypass active' : 'Normal payment flow'}
            </div>
          </Card>
        )}

        {/* Tabs */}
        <div className="mb-6">
          <div className="flex gap-2 border-b border-[#EDE9FF]">
            <button
              onClick={() => setActiveTab('feedbacks')}
              className={`px-6 py-3 font-semibold rounded-t-xl transition-all ${
                activeTab === 'feedbacks'
                  ? 'bg-gradient-to-r from-[#F7B26A] to-[#ECA7C1] text-white'
                  : 'text-[#6B6FA3] hover:text-[#1F224B] hover:bg-[#F3E8FF]'
              }`}
            >
              <MessageCircle className="w-4 h-4 inline mr-2" />
              Feedbacks ({count})
            </button>
            <button
              onClick={() => setActiveTab('users')}
              className={`px-6 py-3 font-semibold rounded-t-xl transition-all ${
                activeTab === 'users'
                  ? 'bg-gradient-to-r from-[#F7B26A] to-[#ECA7C1] text-white'
                  : 'text-[#6B6FA3] hover:text-[#1F224B] hover:bg-[#F3E8FF]'
              }`}
            >
              <Users className="w-4 h-4 inline mr-2" />
              Users ({userCount})
            </button>
            <button
              onClick={() => setActiveTab('makeAdmin')}
              className={`px-6 py-3 font-semibold rounded-t-xl transition-all ${
                activeTab === 'makeAdmin'
                  ? 'bg-gradient-to-r from-[#F7B26A] to-[#ECA7C1] text-white'
                  : 'text-[#6B6FA3] hover:text-[#1F224B] hover:bg-[#F3E8FF]'
              }`}
            >
              <Shield className="w-4 h-4 inline mr-2" />
              Make Admin
            </button>
            <button
              onClick={() => setActiveTab('promoCodes')}
              className={`px-6 py-3 font-semibold rounded-t-xl transition-all ${
                activeTab === 'promoCodes'
                  ? 'bg-gradient-to-r from-[#F7B26A] to-[#ECA7C1] text-white'
                  : 'text-[#6B6FA3] hover:text-[#1F224B] hover:bg-[#F3E8FF]'
              }`}
            >
              <Ticket className="w-4 h-4 inline mr-2" />
              Promo Codes
            </button>
            <button
              onClick={() => setActiveTab('sendSnapshot')}
              className={`px-6 py-3 font-semibold rounded-t-xl transition-all ${
                activeTab === 'sendSnapshot'
                  ? 'bg-gradient-to-r from-[#F7B26A] to-[#ECA7C1] text-white'
                  : 'text-[#6B6FA3] hover:text-[#1F224B] hover:bg-[#F3E8FF]'
              }`}
            >
              <Mail className="w-4 h-4 inline mr-2" />
              Send Snapshot
            </button>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <Card className="p-4 mb-6 bg-red-50 border-2 border-red-200 rounded-xl">
            <p className="text-red-600 font-semibold">{error}</p>
          </Card>
        )}

        {/* Content based on active tab */}
        {activeTab === 'feedbacks' && (
          /* Feedbacks List */
          <div className="space-y-4">
            <h2 className="text-2xl font-bold text-[#1F224B] mb-4 flex items-center gap-3">
              <MessageCircle className="w-6 h-6 text-[#F7B26A]" />
              User Feedbacks
            </h2>
          
          {feedbacks.length === 0 ? (
            <Card className="p-8 text-center bg-white/95 backdrop-blur border-4 border-[#F0EEFF] rounded-3xl shadow-xl">
              <MessageCircle className="w-16 h-16 mx-auto mb-4 text-[#A6A1F7]" />
              <h3 className="text-xl font-semibold text-[#1F224B] mb-2">No Feedbacks Yet</h3>
              <p className="text-[#6B6FA3]">Feedback submissions will appear here</p>
            </Card>
          ) : (
            feedbacks.map((feedback, index) => (
              <Card 
                key={index}
                className="p-6 bg-white/95 backdrop-blur border-2 border-[#F0EEFF] rounded-3xl shadow-md hover:shadow-xl hover:border-[#ECA7C1] transition-all cursor-pointer"
                onClick={() => setSelectedFeedback(selectedFeedback?.timestamp === feedback.timestamp ? null : feedback)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-4 mb-3 flex-wrap">
                      <div className="flex items-center gap-2 text-[#1F224B]">
                        <Mail className="w-4 h-4 text-[#F7B26A]" />
                        <span className="font-semibold">{feedback.email}</span>
                      </div>
                      <div className="flex items-center gap-2 text-[#6B6FA3] text-sm">
                        <Calendar className="w-4 h-4" />
                        <span>{formatDate(feedback.timestamp)}</span>
                      </div>
                    </div>
                    
                    <div className="text-sm text-[#6B6FA3]">
                      <span className="font-semibold text-[#1F224B]">Grade: </span>
                      {feedback.answers['What grade is your child currently in?'] || 'N/A'}
                    </div>

                    {/* Expanded View */}
                    {selectedFeedback?.timestamp === feedback.timestamp && (
                      <div className="mt-4 pt-4 border-t border-[#EDE9FF]">
                        <h4 className="font-bold text-[#1F224B] mb-3">Detailed Feedback:</h4>
                        <div className="space-y-3">
                          {Object.entries(feedback.answers).map(([question, answer]) => (
                            answer && (
                              <div key={question} className="bg-gradient-to-br from-[#F3E8FF] to-[#FFF7EE] p-3 rounded-xl border border-[#EDE9FF]">
                                <p className="font-semibold text-[#1F224B] text-sm mb-1">{question}</p>
                                <p className="text-[#6B6FA3]">{answer}</p>
                              </div>
                            )
                          ))}
                        </div>
                        
                        <div className="mt-4 pt-4 border-t border-[#EDE9FF] text-sm text-[#6B6FA3]">
                          <p><span className="font-semibold text-[#1F224B]">User ID:</span> {feedback.user_id}</p>
                          <p><span className="font-semibold text-[#1F224B]">Child ID:</span> {feedback.child_id}</p>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <Button
                    variant="ghost"
                    size="sm"
                    className="ml-4 text-[#A6A1F7] hover:text-[#8E7BFF] hover:bg-[#F3E8FF]"
                  >
                    {selectedFeedback?.timestamp === feedback.timestamp ? '▲' : '▼'}
                  </Button>
                </div>
              </Card>
            ))
          )}
        </div>
        )}

        {activeTab === 'users' && (
          /* Users List */
          <div className="space-y-4">
            <h2 className="text-2xl font-bold text-[#1F224B] mb-4 flex items-center gap-3">
              <Users className="w-6 h-6 text-[#A6A1F7]" />
              Registered Users
            </h2>
            
            {users.length === 0 ? (
              <Card className="p-8 text-center bg-white/95 backdrop-blur border-4 border-[#F0EEFF] rounded-3xl shadow-xl">
                <Users className="w-16 h-16 mx-auto mb-4 text-[#A6A1F7]" />
                <h3 className="text-xl font-semibold text-[#1F224B] mb-2">No Users Found</h3>
                <p className="text-[#6B6FA3]">User data will appear here when available</p>
              </Card>
            ) : (
              <Card className="bg-white/95 backdrop-blur border-4 border-[#F0EEFF] rounded-3xl shadow-xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gradient-to-r from-[#F3E8FF] to-[#FFF7EE]">
                      <tr>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-[#1F224B]">#</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-[#1F224B]">Email</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-[#1F224B]">Joined Date</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#EDE9FF]">
                      {users.map((user, index) => (
                        <tr key={index} className="hover:bg-[#F3E8FF]/30 transition-colors">
                          <td className="px-6 py-4 text-sm text-[#6B6FA3]">{index + 1}</td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <Mail className="w-4 h-4 text-[#F7B26A]" />
                              <span className="text-sm font-medium text-[#1F224B]">{user.email}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm text-[#6B6FA3]">{user.joinedDate}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}
          </div>
        )}

        {activeTab === 'makeAdmin' && (
          /* Make Admin Section */
          <div className="space-y-4">
            <h2 className="text-2xl font-bold text-[#1F224B] mb-4 flex items-center gap-3">
              <Shield className="w-6 h-6 text-[#8E7BFF]" />
              Grant Admin Access
            </h2>
            
            <Card className="p-8 bg-white/95 backdrop-blur border-4 border-[#F0EEFF] rounded-3xl shadow-xl">
              <div className="max-w-2xl mx-auto">
                <div className="mb-6 text-center">
                  <div className="w-20 h-20 bg-gradient-to-r from-[#A6A1F7] to-[#8E7BFF] rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <UserPlus className="w-10 h-10 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-[#1F224B] mb-2">Add New Administrator</h3>
                  <p className="text-[#6B6FA3]">Enter the email address of the user you want to make an admin</p>
                </div>

                {/* Success/Error Messages */}
                {adminMessage.text && (
                  <Card className={`p-4 mb-6 border-2 rounded-xl ${
                    adminMessage.type === 'success' 
                      ? 'bg-green-50 border-green-200' 
                      : 'bg-red-50 border-red-200'
                  }`}>
                    <p className={`font-semibold ${
                      adminMessage.type === 'success' ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {adminMessage.text}
                    </p>
                  </Card>
                )}

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-[#1F224B] mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      value={newAdminEmail}
                      onChange={(e) => setNewAdminEmail(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleMakeAdmin()}
                      placeholder="user@example.com"
                      disabled={isAddingAdmin}
                      className="w-full px-4 py-3 rounded-xl border-2 border-[#EDE9FF] focus:border-[#A6A1F7] focus:outline-none transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed"
                    />
                  </div>

                  <Button
                    onClick={handleMakeAdmin}
                    disabled={isAddingAdmin || !newAdminEmail.trim()}
                    className="w-full bg-gradient-to-r from-[#A6A1F7] to-[#8E7BFF] text-white py-6 rounded-xl font-bold text-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isAddingAdmin ? (
                      <>
                        <div className="inline-block animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-2"></div>
                        Processing...
                      </>
                    ) : (
                      <>
                        <Shield className="w-5 h-5 inline mr-2" />
                        Make Admin
                      </>
                    )}
                  </Button>
                </div>

                <div className="mt-8 p-4 bg-gradient-to-br from-[#F3E8FF] to-[#FFF7EE] rounded-xl border border-[#EDE9FF]">
                  <h4 className="font-semibold text-[#1F224B] mb-2 flex items-center gap-2">
                    <span className="text-lg">⚠️</span>
                    Important Notes:
                  </h4>
                  <ul className="text-sm text-[#6B6FA3] space-y-1 ml-6">
                    <li className="list-disc">The user must already be registered in the system</li>
                    <li className="list-disc">Admin privileges grant access to all admin features</li>
                    <li className="list-disc">Make sure you trust the user before granting admin access</li>
                  </ul>
                </div>
              </div>
            </Card>
          </div>
        )}

        {activeTab === 'sendSnapshot' && (
          /* Send Snapshot Section */
          <div className="space-y-4">
            <h2 className="text-2xl font-bold text-[#1F224B] mb-4 flex items-center gap-3">
              <Mail className="w-6 h-6 text-[#F7B26A]" />
              Email a Learning Snapshot
            </h2>

            <Card className="p-8 bg-white/95 backdrop-blur border-4 border-[#F0EEFF] rounded-3xl shadow-xl">
              <div className="max-w-2xl mx-auto">
                <p className="text-[#6B6FA3] mb-6">
                  Load a user's snapshot to see it exactly as their page shows it, then send
                  that page as a PDF to any email address.
                </p>

                {snapshotMessage.text && (
                  <Card className={`p-4 mb-6 border-2 rounded-xl ${
                    snapshotMessage.type === 'success'
                      ? 'bg-green-50 border-green-200'
                      : 'bg-red-50 border-red-200'
                  }`}>
                    <p className={`font-semibold ${
                      snapshotMessage.type === 'success' ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {snapshotMessage.text}
                    </p>
                  </Card>
                )}

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-[#1F224B] mb-2">
                      Parent account email
                    </label>
                    <input
                      type="email"
                      value={snapshotParentEmail}
                      onChange={(e) => { setSnapshotParentEmail(e.target.value); setSnapshotPreview(null); }}
                      placeholder="parent@example.com"
                      disabled={isSendingSnapshot || isLoadingPreview}
                      className="w-full px-4 py-3 rounded-xl border-2 border-[#EDE9FF] focus:border-[#A6A1F7] focus:outline-none transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-[#1F224B] mb-2">
                      Child ID <span className="font-normal text-[#6B6FA3]">(only needed if the account has several children)</span>
                    </label>
                    <input
                      type="text"
                      value={snapshotChildId}
                      onChange={(e) => { setSnapshotChildId(e.target.value); setSnapshotPreview(null); }}
                      placeholder="leave blank if there's just one"
                      disabled={isSendingSnapshot || isLoadingPreview}
                      className="w-full px-4 py-3 rounded-xl border-2 border-[#EDE9FF] focus:border-[#A6A1F7] focus:outline-none transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed"
                    />
                  </div>

                  <Button
                    onClick={handleLoadSnapshotPreview}
                    disabled={isLoadingPreview || isSendingSnapshot || !snapshotParentEmail.trim()}
                    className="w-full bg-gradient-to-r from-[#A6A1F7] to-[#8E7BFF] text-white py-4 rounded-xl font-bold hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoadingPreview ? (
                      <>
                        <div className="inline-block animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-2"></div>
                        Loading the snapshot...
                      </>
                    ) : (
                      'Load the snapshot'
                    )}
                  </Button>

                  {snapshotPreview && (
                    <>
                      <div>
                        <label className="block text-sm font-semibold text-[#1F224B] mb-2">
                          Send the letter to
                        </label>
                        <input
                          type="email"
                          value={snapshotToEmail}
                          onChange={(e) => setSnapshotToEmail(e.target.value)}
                          placeholder="recipient@example.com"
                          disabled={isSendingSnapshot}
                          className="w-full px-4 py-3 rounded-xl border-2 border-[#EDE9FF] focus:border-[#A6A1F7] focus:outline-none transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed"
                        />
                      </div>

                      <Button
                        onClick={handleSendSnapshot}
                        disabled={isSendingSnapshot || !snapshotToEmail.trim()}
                        className="w-full bg-gradient-to-r from-[#F7B26A] to-[#F76C8A] text-white py-6 rounded-xl font-bold text-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isSendingSnapshot ? (
                          <>
                            <div className="inline-block animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-2"></div>
                            Capturing and sending...
                          </>
                        ) : (
                          <>
                            <Mail className="w-5 h-5 inline mr-2" />
                            Send this page as PDF
                          </>
                        )}
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </Card>

            {snapshotPreview && (
              <Card className="p-2 bg-white/95 backdrop-blur border-4 border-[#F0EEFF] rounded-3xl shadow-xl overflow-hidden">
                <SnapshotLetter
                  bare
                  letter={snapshotPreview.letter}
                  childId={snapshotPreview.childId}
                  grade={snapshotPreview.grade}
                  prefetchedAnswers={snapshotPreview.answers}
                  captureRef={snapshotCaptureRef}
                />
              </Card>
            )}
          </div>
        )}

        {activeTab === 'promoCodes' && (
          /* Promo Codes Section */
          <div className="space-y-4">
            <h2 className="text-2xl font-bold text-[#1F224B] mb-4 flex items-center gap-3">
              <Ticket className="w-6 h-6 text-[#F7B26A]" />
              Promo Codes
            </h2>

            <p className="text-sm text-[#6B6FA3] max-w-2xl">
              Generate single-use promo codes that let a parent unlock <span className="font-semibold text-[#1F224B]">one child</span> for
              assessments without payment. Each code works exactly once and is then recorded as used.
            </p>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-3">
              <Card className="p-4 bg-white/95 backdrop-blur border-2 border-[#F0EEFF] rounded-2xl shadow-md text-center">
                <p className="text-2xl font-bold text-[#1F224B]">{promoStats.total}</p>
                <p className="text-xs text-[#6B6FA3] font-medium">Total</p>
              </Card>
              <Card className="p-4 bg-white/95 backdrop-blur border-2 border-green-100 rounded-2xl shadow-md text-center">
                <p className="text-2xl font-bold text-green-600">{promoStats.active}</p>
                <p className="text-xs text-[#6B6FA3] font-medium">Active</p>
              </Card>
              <Card className="p-4 bg-white/95 backdrop-blur border-2 border-[#F0EEFF] rounded-2xl shadow-md text-center">
                <p className="text-2xl font-bold text-[#8E7BFF]">{promoStats.used}</p>
                <p className="text-xs text-[#6B6FA3] font-medium">Used</p>
              </Card>
            </div>

            {/* Generate form */}
            <Card className="p-6 bg-white/95 backdrop-blur border-4 border-[#F0EEFF] rounded-3xl shadow-xl">
              <h3 className="text-lg font-bold text-[#1F224B] mb-3">Generate New Codes</h3>
              <div className="flex flex-wrap items-end gap-3">
                <div className="flex-1 min-w-[180px]">
                  <label className="block text-sm font-semibold text-[#1F224B] mb-2">How many codes?</label>
                  <input
                    type="number"
                    min={1}
                    max={500}
                    value={generateCount}
                    onChange={(e) => setGenerateCount(Number(e.target.value))}
                    disabled={isGenerating}
                    className="w-full px-4 py-3 rounded-xl border-2 border-[#EDE9FF] focus:border-[#F7B26A] focus:outline-none transition-colors disabled:bg-gray-100"
                  />
                </div>
                <Button
                  onClick={handleGeneratePromoCodes}
                  disabled={isGenerating || generateCount < 1}
                  className="bg-gradient-to-r from-[#F7B26A] to-[#F76C8A] text-white px-6 py-3 rounded-xl font-bold hover:shadow-xl transition-all disabled:opacity-50"
                >
                  {isGenerating ? (
                    <>
                      <div className="inline-block animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-2"></div>
                      Generating...
                    </>
                  ) : (
                    <>
                      <Ticket className="w-5 h-5 inline mr-2" />
                      Generate
                    </>
                  )}
                </Button>
              </div>

              {promoError && (
                <p className="text-sm text-red-600 mt-4 bg-red-50 border border-red-200 rounded-xl p-3">{promoError}</p>
              )}

              {/* Newly generated codes */}
              {generatedCodes.length > 0 && (
                <div className="mt-6">
                  <h4 className="font-semibold text-[#1F224B] mb-2 flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-600" />
                    {generatedCodes.length} code{generatedCodes.length > 1 ? 's' : ''} generated — share them with parents
                  </h4>
                  <div className="space-y-2">
                    {generatedCodes.map((code) => (
                      <div key={code} className="flex items-center justify-between bg-gradient-to-r from-[#FFF7EE] to-[#FFF0F6] border border-[#F7B26A]/30 rounded-xl px-4 py-3">
                        <code className="font-mono font-bold text-[#1F224B] tracking-wider">{code}</code>
                        <button
                          onClick={() => copyToClipboard(code)}
                          className="flex items-center gap-1 text-sm font-semibold text-[#8E7BFF] hover:text-[#1F224B] transition-colors"
                        >
                          {copiedCode === code ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                          {copiedCode === code ? 'Copied' : 'Copy'}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Card>

            {/* All codes table */}
            <Card className="bg-white/95 backdrop-blur border-4 border-[#F0EEFF] rounded-3xl shadow-xl overflow-hidden">
              <div className="flex items-center justify-between px-6 py-4 border-b border-[#EDE9FF]">
                <h3 className="text-lg font-bold text-[#1F224B]">All Promo Codes</h3>
                <button
                  onClick={loadPromoCodes}
                  disabled={promoLoading}
                  className="text-sm font-semibold text-[#8E7BFF] hover:text-[#1F224B] disabled:opacity-50"
                >
                  {promoLoading ? 'Loading...' : 'Refresh'}
                </button>
              </div>
              <div className="overflow-x-auto">
                {promoCodes.length === 0 ? (
                  <div className="p-8 text-center">
                    <Ticket className="w-12 h-12 mx-auto mb-3 text-[#A6A1F7]" />
                    <p className="text-[#6B6FA3]">No promo codes yet. Generate some above.</p>
                  </div>
                ) : (
                  <table className="w-full">
                    <thead className="bg-gradient-to-r from-[#F3E8FF] to-[#FFF7EE]">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-[#1F224B]">Code</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-[#1F224B]">Status</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-[#1F224B]">Created</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-[#1F224B]">Used By</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-[#1F224B]">Child</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-[#1F224B]">Used At</th>
                        <th className="px-4 py-3"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#EDE9FF]">
                      {promoCodes.map((item) => (
                        <tr key={item.code} className="hover:bg-[#F3E8FF]/30 transition-colors">
                          <td className="px-4 py-3"><code className="font-mono font-bold text-[#1F224B] tracking-wide">{item.code}</code></td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${
                              item.status === 'active'
                                ? 'text-green-700 bg-green-50 border border-green-200'
                                : 'text-[#6B6FA3] bg-[#F5F3FF] border border-[#EDE9FF]'
                            }`}>
                              {item.status === 'active' ? <Unlock className="w-3 h-3" /> : <Lock className="w-3 h-3" />}
                              {item.status}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-[#6B6FA3]">{item.created_at ? formatDate(item.created_at) : '—'}</td>
                          <td className="px-4 py-3 text-sm text-[#6B6FA3]">{item.used_by || '—'}</td>
                          <td className="px-4 py-3 text-sm text-[#6B6FA3]">{item.used_for_child || '—'}</td>
                          <td className="px-4 py-3 text-sm text-[#6B6FA3]">{item.used_at ? formatDate(item.used_at) : '—'}</td>
                          <td className="px-4 py-3 text-right">
                            {item.status === 'active' && (
                              <button
                                onClick={() => copyToClipboard(item.code)}
                                className="flex items-center gap-1 text-sm font-semibold text-[#8E7BFF] hover:text-[#1F224B] ml-auto"
                              >
                                {copiedCode === item.code ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </Card>
          </div>
        )}
      </div>
      
      <footer className="mt-10 text-center text-[#6B6FA3] text-sm">
        © The Dear Parent Project - Admin Panel
      </footer>
    </div>
  );
};
